import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Zap, 
  Share2, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Mic2,
  Image,
  ChevronRight
} from 'lucide-react';
import { Button, FeatureCard, Footer, FeedbackWidget, SupportContact } from './ui';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Sparkles,
      title: 'AI-Generated Quotes',
      description: 'Compelling quotes automatically created from your episode title using advanced AI'
    },
    {
      icon: Image,
      title: 'Beautiful Quote Cards',
      description: 'Professional designs with varied styles, ready for instant social media sharing'
    },
    {
      icon: Share2,
      title: 'Magic Link Sharing',
      description: 'One-click sharing to Twitter, LinkedIn, and more. Your guests will love it'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Enter Episode Details',
      description: 'Add your episode title, guest name, and email. Optionally upload a video clip.'
    },
    {
      number: '02',
      title: 'AI Generates Content',
      description: 'Our AI creates compelling quotes, engaging captions, and beautiful quote cards.'
    },
    {
      number: '03',
      title: 'Guest Gets Magic Link',
      description: 'Your guest receives a beautiful share page with one-click social sharing.'
    }
  ];

  const benefits = [
    'Save hours on promotional content creation',
    'Increase episode shares by 3x',
    'Professional quality, every time',
    'Works with any podcast platform'
  ];

  return (
    <div className="min-h-screen bg-zinc-950 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-20">
          {/* Navigation */}
          <motion.nav 
            className="flex items-center justify-between mb-20"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2">
              <img 
                src="/echokit-logo.png" 
                alt="EchoKit" 
                className="h-14 w-auto"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/pricing')}
              >
                Pricing
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
            </div>
          </motion.nav>

          {/* Hero Content */}
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-8"
            >
              <Zap className="w-4 h-4" />
              Podcast Promotion Made Simple
            </motion.div>

            <motion.h1 
              className="text-5xl md:text-7xl font-bold text-zinc-100 mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Turn Every Guest Into a{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">
                Promoter
              </span>
            </motion.h1>

            <motion.p 
              className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Generate professional promotion kits for your podcast guests in seconds. 
              AI-powered quotes, beautiful graphics, and one-click sharing.
            </motion.p>

            {/* Early Adopter Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mb-10"
            >
              <div className="mx-auto max-w-4xl">
                <div className="flex flex-col lg:flex-row items-center justify-center gap-4 rounded-2xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 px-6 py-4 backdrop-blur-sm">
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-xs font-medium shadow-lg shadow-emerald-500/10">
                      Early Adopter
                    </span>
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm font-bold">
                      <span className="drop-shadow-[0_0_8px_rgba(16,185,129,0.4)] hover:drop-shadow-[0_0_12px_rgba(16,185,129,0.6)] transition-all duration-300">$9.99</span>
                      <span className="text-emerald-400/80">(50% off)</span>
                    </span>
                  </div>
                  <span className="text-sm text-zinc-200 text-center lg:text-left max-w-md">
                    Lock in your founding-member rate forever and get priority access to <span className="text-emerald-300 font-semibold">Autopost</span> and <span className="text-emerald-300 font-semibold">Faster kit generation</span>.
                  </span>
                  <Button
                    size="sm"
                    icon={ArrowRight}
                    iconPosition="right"
                    onClick={() => navigate('/pricing')}
                    className="min-w-[200px] shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-300"
                  >
                    Claim my founder rate
                  </Button>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Button
                size="xl"
                icon={ArrowRight}
                iconPosition="right"
                onClick={() => navigate('/login')}
                className="min-w-[200px]"
              >
                Early Access
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              className="mt-16 flex items-center justify-center gap-8 text-sm text-zinc-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-500" />
                <span>Setup in 60 seconds</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-zinc-700 flex items-start justify-center p-2">
            <div className="w-1.5 h-2.5 rounded-full bg-emerald-500" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-32">
        <div className="absolute inset-0 bg-zinc-900/50" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-100 mb-4">
              Everything You Need
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Stop spending hours on promotional content. Let EchoKit handle it.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <FeatureCard {...feature} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/50 to-zinc-950" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <motion.div 
            className="text-center mb-20"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-100 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-zinc-400">
              Three simple steps to podcast promotion success
            </p>
          </motion.div>

          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div 
                key={index}
                className="flex flex-col md:flex-row items-start gap-8 p-8 rounded-2xl bg-zinc-900/30 border border-zinc-800/50"
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <span className="text-2xl font-bold text-emerald-400">{step.number}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-zinc-100 mb-3">{step.title}</h3>
                  <p className="text-zinc-400 text-lg leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-emerald-950/10 to-zinc-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(16,185,129,0.1)_0%,transparent_70%)]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-100 mb-12">
              Why Podcasters Love EchoKit
            </h2>

            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-zinc-300">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32">
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-zinc-950" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-8">
              <Mic2 className="w-10 h-10 text-emerald-400" />
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-zinc-100 mb-6">
              Ready to Transform Your<br />Podcast Promotion?
            </h2>
            
            <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto">
              Join podcasters who are saving hours every week and getting more shares from their guests.
            </p>

            <Button
              size="xl"
              icon={ChevronRight}
              iconPosition="right"
              onClick={() => navigate('/login')}
              className="min-w-[250px]"
            >
              Start Creating Kits Now
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Feedback Widget */}
      <FeedbackWidget />

      {/* Support Contact */}
      <SupportContact />
    </div>
  );
}

export default LandingPage;
